import React from 'react';
import HeaderContainer from '../../../../v2BaseComponents/HeaderContainer';
import Stack from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import Chip from '../../../../v2BaseComponents/Chip';
import useResource from '../../../../../hooks/api/useResource';
import { MedicalDataTypes } from '../../../../../primary-record-types';
import { resourceTypeGuards } from '../../../../../utils/medicalDataUtils';
import { getIntl } from '../../../../../utils/intl';
import getName from '../../../../../utils/getName';
import getDateString, { getMinimalDateTimeSpan } from '../../../../../utils/getDateString';
import { allergyTypeMessages, reactionTypeMessages, getVisitTypeDisplay } from '../../../../../utils/sharedMessages';
import { getDosageInstructions } from '../../../../../utils/getDosageInstructions';
import { toTitleCase } from '../../../../../utils/toTitleCase';
import { getSpacing } from '../../../../../utils/spacing';
import { useResourceTitle } from '../../../../../utils/resourceUtils/getResourceTitle';

const intl = getIntl('resource-details-header', {
    fromIntegration: 'From {integration}',
    fromPatientPortal: 'From Patient Portal',
    fromUser: 'Added by {name}',
    startedOn: 'Started: {date}',
    visit: 'Visit',
});

export type ResourceDetailsHeaderOwnProps = {
    resourceId: string;
    resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
    profileId: string;
};

export type ResourceDetailsHeaderProps = ResourceDetailsHeaderOwnProps;

const Header = ({
    title,
    secondaryText,
    mutedText,
    tagText: chipText,
}: {
    title: string;
    secondaryText?: string;
    mutedText?: string;
    tagText?: string;
}) => {
    return (
        <HeaderContainer>
            <Stack vertical space={getSpacing(0)}>
                <Text variant="h4">{title}</Text>
                {chipText ? <Chip title={chipText} /> : null}
                {secondaryText ? <Text variant="caption-1">{secondaryText}</Text> : null}
                {mutedText ? <Text variant="caption-2">{mutedText}</Text> : null}
            </Stack>
        </HeaderContainer>
    );
};

const ResourceDetailsHeader: React.FC<ResourceDetailsHeaderProps> = ({
    resourceId,
    resourceCategory: category,
    profileId,
}) => {
    const { resource } = useResource({
        medicalProfileId: profileId,
        resourceCategory: category,
        resourceId,
    });
    const visitName = useResourceTitle(resource?.resource || null, profileId);

    if (!resource) return null;

    let sourceText: string | undefined;
    if (resource.metadata.source === '1uphealth') {
        sourceText = resource.metadata.healthSystemName
            ? intl('fromIntegration', { integration: resource.metadata.healthSystemName })
            : intl('fromPatientPortal');
    } else if (resource.metadata.source === 'fasten') {
        sourceText = resource.metadata.institutionName
            ? intl('fromIntegration', { integration: resource.metadata.institutionName })
            : intl('fromPatientPortal');
    } else if (resource.createdByUser) {
        sourceText = intl('fromUser', { name: getName(resource.createdByUser) });
    }

    if (resourceTypeGuards.isCondition(resource.resource)) {
        return (
            <Header
                title={resource.resource.name}
                secondaryText={
                    resource.resource.startDateTime
                        ? intl('startedOn', { date: getDateString(resource.resource.startDateTime, 'date') })
                        : undefined
                }
                mutedText={sourceText}
            />
        );
    } else if (resourceTypeGuards.isAllergy(resource.resource)) {
        let tagText: string | undefined = undefined;
        if (resource.resource.allergyType) {
            tagText = allergyTypeMessages(resource.resource.allergyType);
            if (resource.resource.reactionType) {
                tagText += ` - ${reactionTypeMessages(resource.resource.reactionType)}`;
            }
        }
        return (
            <Header
                title={resource.resource.name}
                tagText={tagText}
                secondaryText={
                    resource.resource.startDateTime
                        ? intl('startedOn', { date: getDateString(resource.resource.startDateTime, 'date') })
                        : undefined
                }
                mutedText={sourceText}
            />
        );
    } else if (resourceTypeGuards.isMedication(resource.resource)) {
        const dosageText = getDosageInstructions(resource.resource);

        const dateText = resource.resource.startDateTime
            ? intl('startedOn', { date: getDateString(resource.resource.startDateTime, 'date') })
            : undefined;
        const secondaryText = dosageText || dateText;
        return (
            <Header
                title={toTitleCase(resource.resource.name, { ignoreAllCaps: true })}
                secondaryText={secondaryText}
                mutedText={sourceText}
            />
        );
    } else if (resourceTypeGuards.isVisit(resource.resource)) {
        return (
            <Header
                title={visitName || intl('visit')}
                tagText={getVisitTypeDisplay(resource.resource) || undefined}
                secondaryText={getMinimalDateTimeSpan(resource?.resource.startDateTime, resource?.resource.endDateTime)}
                mutedText={sourceText}
            />
        );
    } else if (resourceTypeGuards.isImaging(resource.resource)) {
        return (
            <Header
                title={resource.resource.name}
                secondaryText={resource.resource.dateTime ? getDateString(resource.resource.dateTime, 'date') : ''}
                mutedText={sourceText}
            />
        );
    } else if (resourceTypeGuards.isProcedure(resource.resource)) {
        return (
            <Header
                title={resource.resource.name}
                secondaryText={resource.resource.dateTime ? getDateString(resource.resource.dateTime, 'date') : ''}
                mutedText={sourceText}
            />
        );
    } else if (resourceTypeGuards.isVaccination(resource.resource)) {
        return (
            <Header
                title={resource.parentCoding?.display || resource.resource.vaccineType.display}
                secondaryText={resource.resource.dateTime ? getDateString(resource.resource.dateTime, 'date') : ''}
                mutedText={sourceText}
            />
        );
    }
};

export default React.memo(ResourceDetailsHeader);
